// /src/App.js

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import SidebarMenu from './components/layout/SidebarMenu';
import Login from './components/Login';
import Signup from './components/Signup';
import FAQManagement from './components/FAQManagement';
import AIChat from './components/AIChat';
import PropertyManagement from './components/PropertyManagement';
import QRCodeManagement from './components/QRCodeManagement';
import ProtectedRoute from './components/ProtectedRoute';
import GuestChat from './components/GuestChat';
import UserManagement from './components/UserManagement';
import InvitationManagement from './components/InvitationManagement';
import Dashboard from './components/Dashboard';
import ProfileManagement from './components/ProfileManagement';
import LandingPage from './components/marketing/LandingPage';
import { Toaster } from 'sonner';
import { toastConfig } from './config/toastConfig';
import EmailVerification from './components/EmailVerification';
import './styles/globals.css'
import './i18n';

const AppLayout = ({ children, isAuthenticated, setIsAuthenticated, userRole }) => {
  const location = useLocation();
  
  // List of routes where we don't want to show the sidebar
  const noSidebarRoutes = ['/landing', '/login', '/signup', '/verify'];
  
  // Check if current route should have sidebar
  const shouldShowSidebar = !noSidebarRoutes.includes(location.pathname);

  return (
    <div className="flex h-screen bg-gray-900">
      {isAuthenticated && shouldShowSidebar && (
        <SidebarMenu
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          userRole={userRole}
        />
      )}
      <main className={`flex-1 ${!shouldShowSidebar ? 'w-full' : ''} ${
        location.pathname === '/verify' ? 'h-screen' : 'overflow-y-auto'
      } bg-gray-900`}>
        {children}
      </main>
    </div>
  );
};

const VerificationRoute = ({ setIsAuthenticated, setUserRole }) => {
  const location = useLocation();
  const email = location.state?.email || localStorage.getItem('userEmail');

  // Redirect to signup if no email in state or localStorage
  if (!email) {
    return <Navigate to="/signup" replace />;
  }

  return (
    <div className="h-full">  {/* Add this wrapper */}
      <EmailVerification 
        email={email}
        onVerificationSuccess={() => {
          setIsAuthenticated(true);
          setUserRole('user');
        }}
      />
    </div>
  );
};

const AppContent = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthData = () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const role = localStorage.getItem('role');
      const guestToken = localStorage.getItem('guestToken');
      
      if (token && !userId) {
        console.log('Missing userId, clearing auth data');
        localStorage.clear();
        setIsAuthenticated(false);
        setUserRole(null);
        navigate('/login');
        return;
      }
      
      if (token && role) {
        setIsAuthenticated(true);
        setUserRole(role);
      } else if (guestToken && role === 'guest') {
        setIsAuthenticated(true);
        setUserRole('guest');
      }
      setIsLoading(false);
    };

    checkAuthData();
  }, [navigate]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Toaster {...toastConfig} />
      <AppLayout 
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        userRole={userRole}
      >
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={
              <Login setIsAuthenticated={setIsAuthenticated} setUserRole={setUserRole} />
            } />
            <Route 
              path="/verify" 
              element={
                <VerificationRoute 
                  setIsAuthenticated={setIsAuthenticated}
                  setUserRole={setUserRole}
                />
              } 
            />
            <Route path="/signup" element={
              <Signup setIsAuthenticated={setIsAuthenticated} setUserRole={setUserRole} />
            } />
              <Route 
                path="/user-management" 
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin']}>
                    <UserManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                    <ProfileManagement />
                  </ProtectedRoute>
                } 
              />
              <Route path="/dashboard" element={
                <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/invitation-management" element={
                <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                  <InvitationManagement />
                </ProtectedRoute>
              } />
              <Route path="/properties" element={
                <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                  <PropertyManagement />
                </ProtectedRoute>
              } />
              <Route 
                path="/qrcodes" 
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                    <QRCodeManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/qrcodes/:propertyId" 
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                    <QRCodeManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/faqs" 
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                    <FAQManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/faqs/:propertyId" 
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user']}>
                    <FAQManagement />
                  </ProtectedRoute>
                } 
              />
              <Route path="/chat" element={
                <ProtectedRoute isAuthenticated={isAuthenticated} userRole={userRole} allowedRoles={['admin', 'user', 'guest']}>
                  <AIChat />
                </ProtectedRoute>
              } />
              <Route 
                path="/guest-chat/:shortId" 
                element={
                  <GuestChat 
                    setIsAuthenticated={setIsAuthenticated}
                    setUserRole={setUserRole}
                  />
                } 
              />
            </Routes>
      </AppLayout>
    </>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;